import _ from 'lodash';
import FormElement from './FormElement';
import StyleMixin from './mixins/styleMixins/StyleMixin';
import mixin from '../privates/utils/mixin';
import DisabledMixin from './mixins/DisabledMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import FocusMixin from './mixins/FocusMixin';
import ClickableMixin from './mixins/ClickableMixin';
import * as Utils from './Utils';
import {nullIfNil, emptyStringIfNil, getIndexOrUndefined} from '../privates/utils';
import {validate, validateRange} from '../privates/validations/validation';
import * as members from '../privates/privateMembers';
import {runValidations, runValidationsAfterChange} from '../privates/validationsService/validationsService';
import valueSanitzationAlgorithm from '../privates/utils/valueSanitzationAlgorithm';
import {getOptionsFromFull, emptyStringIfNotInOptions, optionsToDisplay, validateOption} from '../privates/utils/optionsUtils';
import RequiredMixin from './mixins/RequiredMixin';

/**
 * @class Dropdown
 * @summary Dropdowns are used for selecting one of a number of options.
 *  They are especially useful when there are too many options to display using
 *  [radio buttons]($w.RadioButtonGroup.html). Dropdowns consist of a list
 *  of [options](#Option). Each [option](#Option) contains a label, which is
 *  what the user sees, and a value, which is what is used in code and stored in
 *  you collections.
 * @mixes $w.FormElement
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.DisabledMixin
 * @mixes $w.FocusMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.StyleMixin
 * @mixes $w.RequiredMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.inputs.ComboBoxInput
 * @definitionId wysiwyg.viewer.components.inputs.ComboBoxInput
 * @tagname dropdown
 */
class DropDown extends FormElement {

    /**
     * @typedef {external:Object} $w.Dropdown~Option
     * @summary An object used by the `options` property that contains the attributes of a dropdown list item.
     * @property {external:String} value The value of the dropdown option. This is what you use in code and is what is stored in your collections.
     * @property {external:String} [label] The label of the dropdown option. This is what a user sees.
     * @snippet [Option.es6=Get option info]
     * @see [options](#Options)
     * @see [value](#value)
     */

    /**
     * @member options
     * @label property
     * @syntax
       * get options(): Option[]
       * set options(value: Options[]): void
     * @summary Sets or gets the options in a dropdown.
     * @description
     *  Setting the `options` property sets all the [options](#Option)
     *  available to a user.
     *
     *  Set `options` to an empty array (`[]`) to remove the current dropdown options.
     *
     *  Getting the `options` property returns the current
     *  list of [options](#Option) available to a user.
     *
     *  You cannot modify the options array in-place. To add, change, or remove
     *  individual dropdown options:
     *
     *    1. Store the value of the `options` property in a variable.
     *    1. Make changes to the options array.
     *    1. Reset the `options` property with the modified array.
     * @snippet [DropDown-options_get.es6=Get the list of options and the first option's label and value from dropdown]
     * @snippet [DropDown-options_set.es6=Set the list of options for a dropdown]
     * @snippet [DropDown-add.es6=Add an option to a dropdown]
     *  This example retrieves the options of a dropdown, adds a new
     *  option, and then overwrites the old options.
     * @type {$w.Dropdown~Option[]}
     * @default An empty array
     * @memberof $w.Dropdown
     * @see [Option](#Option)
     * @instance
     */
    get options () {
        const options = Utils.getData(this, 'options');

        return options ? options.map((option) => ({label: option.text, value: option.value})) : options;
    }

  /**
   * @member options
   * @memberof $w.Dropdown
   * @param {$w.Dropdown~Option[]} newOptions
   */
    set options(newOptions) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        const isArrayValid = validate({
            compType,
            functionName: 'options',
            dataToValidate: {options: newOptions},
            compName: this.id
        });
        if (!isArrayValid) {
            return;
        }
        const arrItemsArrValid = _.every(newOptions, (v, i) => {
            return validate({
                compType,
                functionName: 'options',
                dataToValidate: {selectOption: v},
                shouldCheckMandatoryFields: true,
                itemIndex: i,
                compName: this.id
            }) && validateOption(v, i, 'selectOption');
        });
        if (!arrItemsArrValid) {
            return;
        }

        const options = nullIfNil(newOptions) ? optionsToDisplay(newOptions) : [];
        const modifiedOptions = options.map((option) => {
            return {
                value: option.value,
                text: option.label,
                type: 'SelectOption',
                description: option.label,
                disabled: false
            };
        });

        const valToValidate = emptyStringIfNotInOptions(modifiedOptions, RMI.getData(compId).value || RMI.getData(compId).tempValue);
        runValidations(RMI, compId, valToValidate);
        const data = {options: modifiedOptions};
        if (valToValidate) {
            data.value = valToValidate;
            data.tempValue = undefined;
        }
        Utils.setData(this, data);
    }

    /**
     * @member placeholder
     * @label property
     * @syntax
       * get placeholder: string
       * set placeholder(value: string): void
     * @summary Sets or gets the dropdown's placeholder text.
     * @description
     *  Placeholder text is typically used to
     *  provide a hint to the user describing what the dropdown is for.
     *  When the user chooses an option, the placeholder text disappears.
     * @type {external:String}
     * @snippet [DropDown-placeholder_get.es6=Get a dropdown's placeholder text]
     * @snippet [DropDown-placeholder_set.es6=Set a dropdown's placeholder text]
     * @memberof $w.Dropdown
     * @instance
     * @attr {external:String}
     */
    get placeholder() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const placeholder = RMI.getData(compId).placeholder || RMI.getProps(compId).placeholder || {};
        return placeholder.value || '';
    }

  /**
   * @member placeholder
   * @memberof $w.Dropdown
   * @param {external:String} placeholderToSet
   */
    set placeholder(placeholderToSet) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'placeholder',
            dataToValidate: {placeholder: placeholderToSet},
            compName: this.id
        });
        if (!isValid) {
            return;
        }
        placeholderToSet = emptyStringIfNil(placeholderToSet);
        RMI.setData(compId, {
            placeholder: {
                text: placeholderToSet,
                value: placeholderToSet
            }
        });
        RMI.setProps(compId, {
            placeholder: {
                text: placeholderToSet,
                value: placeholderToSet
            }
        });
    }

    /**
     * @member selectedIndex
     * @label property
     * @syntax
       * get selectedIndex: Number
       * set selectedIndex(value: Number): void
     * @summary Sets or gets the index of the selected option.
     * @description
     *  Setting the `selectedIndex` property sets the option at
     *  that index to be the selected option. To set one of the options to be
     *  selected, set the `selectedIndex` property to an index between `0` and
     *  `options.length - 1`.
     *
     *  To reset the dropdown to have no option
     *  selected, set the `selectedIndex` property to `undefined`.
     *
     *  Getting the `selectedIndex` property returns the index of
     *  the currently selected option. If no value is selected, the
     *  `selectedIndex` property returns `undefined`.
     * @type {external:Number}
     * @snippet [DropDown-selectedIndex_get.es6=Get the index of the selected option]
     * @snippet [DropDown-selectedIndex_set.es6=Select the first option by index]
     * @memberof $w.Dropdown
     * @instance
     * @attr {external:String}
     */
    get selectedIndex() {
        return getIndexOrUndefined(this.options, 'value', Utils.getData(this, 'value'));
    }

  /**
   * @member selectedIndex
   * @memberof $w.Dropdown
   * @param {external:Number} index
   */
    set selectedIndex(index) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;

        const options = getOptionsFromFull.call(this);
        const acceptNil = true;
        if (!validate({
            compType,
            functionName: 'selectedIndex',
            dataToValidate: {selectedIndex: index},
            compName: this.id
        }) || !validateRange({
            functionName: 'selectedIndex',
            propertyName: 'selectedIndex',
            value: index,
            minValue: 0,
            maxValue: options.length - 1,
            acceptNil
        })) {
            return;
        }

        const value = _.isNil(index) ? '' : options[index].value;

        runValidationsAfterChange(RMI, compId, value);
        Utils.setData(this, {value});
    }

    /**
     * @member value
     * @label property
     * @syntax
     * get value(): string
     * set value(value: string): void
     * @summary Sets or gets an element's value.
     * @note
     *  Changing a dropdown's `value` in code does not trigger an `onChange` event.
     *
     *  If an element is connected to a dataset, setting the element's `value`
     *  in code does not set the value of the connected field in the dataset.
     *  That means if you use the dataset to perform a submit, the value changed
     *  in code is not reflected in the submitted item.
     *
     *  To submit the new value using a dataset, set the field's value using the
     *  [`setFieldValue()`](wix-dataset.html#setFieldValue) function before
     *  performing the submit.
     * @snippet [FormElement-value_get.es6=Get an element's value]
     * @snippet [FormElement-value_set.es6=Set an element's value]
     * @type {external:String}
     * @memberof $w.Dropdown
     * @override
     */
    get value() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const value = RMI.getData(compId).value;

        return emptyStringIfNotInOptions(this.options, value);
    }

  /**
   * @member value
   * @memberof $w.Dropdown
   * @param {external:String} val
   */
    set value(val) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getFullDataWithOverrides(compId);
        const compType = members._type.get(this).compType;
        const value = valueSanitzationAlgorithm(compData, val);
        const isValid = validate({
            compType,
            functionName: 'value',
            dataToValidate: {value},
            compName: this.id
        });

        if (!isValid) {
            return;
        }
        const valToValidate = emptyStringIfNotInOptions(this.options, value);
        runValidationsAfterChange(RMI, compId, valToValidate);
        const tempValue = valToValidate !== value ? value : undefined;
        RMI.setData(compId, {value: valToValidate, tempValue});
    }

    toJSON() {
        const {id, type, global, rendered, required, value, options, placeholder, selectedIndex} = this;
        return {id, type, global, rendered, required, value, options, placeholder, selectedIndex};
    }

    /**
     * @member style
     * @label property
     * @syntax
     * get style(): Style
     * @summary Gets an object containing information about the dropdown's styles.
     * @description
     *  The following styles can be used with dropdowns:
     *
     *  + [`backgroundColor`]($w.Style.html#backgroundColor)
     *  + [`borderColor`]($w.Style.html#borderColor)
     *  + [`borderRadius`]($w.Style.html#borderRadius)
     *  + [`borderWidth`]($w.Style.html#borderWidth)
     *  + [`color`]($w.Style.html#color)
     *
     *
     *  Getting or setting a dropdown's styles, gets or sets the styles of the
     *  dropdown's regular state. It does not set the styles of the dropdown's hover,
     *  focus, error, or disabled states.
     * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
     * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
     * @type {$w.Style}
     * @memberof $w.Dropdown
     * @readonly
     * @override
     */
}

mixin(DropDown.prototype, HiddenCollapsedMixin.prototype);
mixin(DropDown.prototype, DisabledMixin.prototype);
mixin(DropDown.prototype, FocusMixin.prototype);
mixin(DropDown.prototype, ClickableMixin.prototype);
mixin(DropDown.prototype, StyleMixin.prototype);
mixin(DropDown.prototype, RequiredMixin.prototype);

export default DropDown;
